import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import {
  LayoutDashboard, FilePlus, Library, Users, Stethoscope, Banknote,
  Trash2, Loader2, FileText, Play, Upload, Inbox, Mail, Phone, MapPin, UserPlus, Gift, Send, MessageSquareWarning, Download, MessageCircleHeart, ChefHat, Megaphone, ImagePlus, Tags, Plus, GripVertical, Pencil, X, BadgeCheck, Handshake, Receipt
} from "lucide-react";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "content", label: "Content Library", icon: Library },
  { id: "upload", label: "Add Content", icon: FilePlus },
  { id: "enquiries", label: "Therapist Enquiries", icon: Inbox },
  { id: "leads", label: "Learn More Leads", icon: Megaphone },
  { id: "messages", label: "Messages", icon: MessageCircleHeart },
  { id: "users", label: "Users", icon: Users },
  { id: "practitioners", label: "Practitioners", icon: Handshake },
  { id: "feedback", label: "Feedback", icon: MessageSquareWarning },
];

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

function PromoQrButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/promo-qr", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "image/png" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mbbc-promo-qr.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Promo QR code downloaded");
    } catch {
      toast.error("Could not generate the QR code");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy} data-testid="promo-qr-download"
      className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60"
      style={{ background: "rgba(201,162,39,0.18)", color: "#8a6d12" }}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      Promo page QR code
    </button>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)); }, []);

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const { data: res } = await api.post("/admin/test-email");
      if (res.ok) toast.success(`Test email sent to ${res.sent_to} from ${res.sender_email} — check your inbox (and junk)`);
      else toast.error(`Email FAILED. Sending from: ${res.sender_email} (key ${res.resend_key_ending}). Error: ${res.error}`, { duration: 20000 });
    } catch { toast.error("Could not run the email test"); }
    finally { setTestingEmail(false); }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const stats = [
    { icon: Users, label: "Clients", value: data.total_clients, accent: "bg-teal/10 text-teal" },
    { icon: Stethoscope, label: "Therapists", value: data.total_nts, accent: "bg-navy/10 text-navy" },
    { icon: Library, label: "Content items", value: data.total_content, accent: "bg-amber-100 text-amber-600" },
    { icon: Banknote, label: "Direct online revenue (100%)", value: money(data.direct_revenue), accent: "bg-emerald-100 text-emerald-600" },
  ];
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="bg-navy text-white rounded-xl p-6 shadow-sm" data-testid="member-count-card">
        <div className="flex flex-wrap gap-8 items-start justify-between">
          <div className="flex flex-wrap gap-10">
            <div>
              <span className="text-xs uppercase tracking-wide text-white/60">Active paid members</span>
              <div className="mt-1 font-heading text-4xl" data-testid="active-members-count">{data.active_members ?? 0}</div>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-white/60">Paused</span>
              <div className="mt-1 font-heading text-4xl text-white/80" data-testid="paused-members-count">{data.paused_members ?? 0}</div>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-white/60">Complimentary</span>
              <div className="mt-1 font-heading text-4xl text-white/80" data-testid="complimentary-members-count">{data.complimentary_members ?? 0}</div>
            </div>
          </div>
          <div className="min-w-[260px] flex-1">
            <span className="text-xs uppercase tracking-wide text-white/60">Recent joins</span>
            {(data.recent_joins || []).length === 0 ? (
              <p className="mt-2 text-sm text-white/60" data-testid="recent-joins-empty">No paid members yet.</p>
            ) : (
              <ul className="mt-2 space-y-1.5" data-testid="recent-joins-list">
                {data.recent_joins.map((m) => (
                  <li key={m.email} className="flex justify-between gap-4 text-sm">
                    <span className="truncate">{m.name || m.email}</span>
                    <span className="text-white/60 capitalize shrink-0">
                      {m.membership_plan}{m.membership_status === "paused" ? " · paused" : ""}
                      {m.created_at ? ` · ${new Date(m.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-[#E7E5E4] rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-[#78716C]">{s.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.accent}`}><s.icon className="w-4 h-4" /></div>
            </div>
            <div className="mt-3 font-heading text-3xl text-navy">{s.value}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid="direct-revenue-card">
          <h3 className="font-heading text-lg text-navy">Direct online clients</h3>
          <p className="text-xs text-[#78716C] mt-0.5">Subscriptions with no therapist referral — 100% to MBBC</p>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between bg-emerald-50 rounded-lg px-4 py-3"><span className="text-[#57534E]">Revenue (100% MBBC)</span><span className="font-semibold text-emerald-700">{money(data.direct_revenue)}</span></div>
            <div className="flex justify-between bg-[#F5F5F4] rounded-lg px-4 py-3"><span className="text-[#57534E]">Paid transactions</span><span className="font-semibold text-navy">{data.direct_transactions}</span></div>
          </div>
        </div>
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid="therapist-revenue-card">
          <h3 className="font-heading text-lg text-navy">Recruited therapists</h3>
          <p className="text-xs text-[#78716C] mt-0.5">Referral-attributed subscriptions — therapists keep 80%, MBBC 20%</p>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between bg-[#F5F5F4] rounded-lg px-4 py-3"><span className="text-[#57534E]">Gross revenue</span><span className="font-semibold text-navy">{money(data.therapist_gross_revenue)}</span></div>
            <div className="flex justify-between bg-emerald-50 rounded-lg px-4 py-3"><span className="text-[#57534E]">MBBC share (20%)</span><span className="font-semibold text-emerald-700">{money(data.therapist_mbbc_share)}</span></div>
            <div className="flex justify-between bg-[#F5F5F4] rounded-lg px-4 py-3"><span className="text-[#57534E]">Paid transactions</span><span className="font-semibold text-navy">{data.therapist_transactions}</span></div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid="promo-tools-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg text-navy">Promo page tools</h3>
            <p className="text-xs text-[#78716C] mt-0.5">The exclusive offers page lives at <strong>mybestbodyclub.com/promo</strong> (hidden from navigation) until Friday 23rd October, 9pm. Download the QR code to show on screen at your presentation — people scan it with their phone camera and land straight on the offers.</p>
          </div>
          <PromoQrButton />
        </div>
      </div>
      <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid="email-health-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg text-navy">Email system check</h3>
            <p className="text-xs text-[#78716C] mt-0.5">Sends a test email to your admin address and reports the exact error if sending fails in this environment.</p>
          </div>
          <button onClick={testEmail} disabled={testingEmail} data-testid="test-email-btn"
            className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-dark disabled:opacity-60">
            {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test email
          </button>
        </div>
      </div>
      <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm">
        <h3 className="font-heading text-lg text-navy">Platform totals</h3>
        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
          <div className="flex justify-between bg-[#F5F5F4] rounded-lg px-4 py-3"><span className="text-[#57534E]">Gross revenue (all)</span><span className="font-semibold text-navy">{money(data.gross_revenue)}</span></div>
          <div className="flex justify-between bg-[#F5F5F4] rounded-lg px-4 py-3"><span className="text-[#57534E]">Total MBBC revenue</span><span className="font-semibold text-navy">{money(data.mbbc_revenue)}</span></div>
          <div className="flex justify-between bg-[#F5F5F4] rounded-lg px-4 py-3"><span className="text-[#57534E]">Paid transactions</span><span className="font-semibold text-navy">{data.transactions}</span></div>
        </div>
      </div>
    </div>
  );
}

async function uploadImageFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${api.defaults.baseURL}/images/${data.id}`;
}

async function uploadPdfFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/recipe-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { url: `${api.defaults.baseURL}/files/${data.id}`, name: data.filename || file.name };
}

function RecipeOfWeekEditor({ onPublished }) {
  const [form, setForm] = useState({ title: "", description: "", image_url: "", pdf_url: "" });
  const [pdfName, setPdfName] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/recipe-of-week").then((r) => {
      if (r.data) setForm({ title: r.data.title || "", description: r.data.description || "", image_url: r.data.image_url || "", pdf_url: r.data.pdf_url || "" });
    }).finally(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/recipe-of-week", form);
      toast.success("Recipe of the week updated — members will see it in the portal and archive");
      onPublished?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the Recipe of the Week from the member portal?")) return;
    await api.delete("/admin/recipe-of-week");
    setForm({ title: "", description: "", image_url: "", pdf_url: "" });
    setPdfName("");
    toast.success("Recipe of the week removed");
  };

  const cls = "w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";
  if (!loaded) return null;
  return (
    <form onSubmit={save} className="mb-8 bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm" data-testid="recipe-week-editor">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center"><ChefHat className="w-5 h-5" /></div>
        <div>
          <h3 className="font-heading text-xl text-navy">Recipe of the Week</h3>
          <p className="text-xs text-[#78716C]">Shown at the top of the member portal. Update it anytime.</p>
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <input data-testid="recipe-title" required placeholder="Recipe title (e.g. Rainbow Beetroot Salad)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={cls} />
        <input data-testid="recipe-image" placeholder="Image URL (or upload below)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={cls} />
      </div>
      <textarea data-testid="recipe-description" rows={6} placeholder="Description — why it's great for metabolic health" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${cls} mt-4`} />
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <label data-testid="recipe-image-upload" className="inline-flex items-center gap-2 border-2 border-dashed border-[#E7E5E4] hover:border-teal rounded-lg px-4 py-2.5 text-sm text-[#57534E] cursor-pointer transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin text-teal" /> : <ImagePlus className="w-4 h-4 text-teal" />}
          {uploading ? "Uploading…" : "Upload a photo of the recipe"}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={async (e) => {
            const f = e.target.files[0];
            e.target.value = "";
            if (!f) return;
            setUploading(true);
            try {
              const url = await uploadImageFile(f);
              setForm((s) => ({ ...s, image_url: url }));
              toast.success("Photo uploaded");
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail) || "Image upload failed");
            } finally { setUploading(false); }
          }} />
        </label>
        {form.image_url && (
          <div className="flex items-center gap-2">
            <img src={form.image_url} alt="Recipe preview" data-testid="recipe-image-preview" className="w-14 h-14 rounded-lg object-cover border border-[#E7E5E4]" />
            <button type="button" data-testid="recipe-image-remove" onClick={() => setForm({ ...form, image_url: "" })} className="text-xs text-[#A8A29E] hover:text-red-500">Remove photo</button>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <label data-testid="recipe-pdf-upload" className="inline-flex items-center gap-2 border-2 border-dashed border-[#E7E5E4] hover:border-teal rounded-lg px-4 py-2.5 text-sm text-[#57534E] cursor-pointer transition-colors">
          {pdfUploading ? <Loader2 className="w-4 h-4 animate-spin text-teal" /> : <FileText className="w-4 h-4 text-teal" />}
          {pdfUploading ? "Uploading…" : form.pdf_url ? "Replace the recipe PDF" : "Upload a PDF of the recipe"}
          <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={pdfUploading} onChange={async (e) => {
            const f = e.target.files[0];
            e.target.value = "";
            if (!f) return;
            setPdfUploading(true);
            try {
              const fd = new FormData();
              fd.append("file", f);
              const { data } = await api.post("/admin/recipe-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
              setForm((s) => ({ ...s, pdf_url: `${api.defaults.baseURL}/files/${data.id}` }));
              setPdfName(data.filename || f.name);
              toast.success("Recipe PDF uploaded — members will get a View/Download button");
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail) || "PDF upload failed");
            } finally { setPdfUploading(false); }
          }} />
        </label>
        {form.pdf_url && (
          <div className="flex items-center gap-2 text-sm">
            <a href={form.pdf_url} target="_blank" rel="noreferrer" data-testid="recipe-pdf-link" className="inline-flex items-center gap-1.5 text-teal hover:text-teal-dark font-medium">
              <FileText className="w-4 h-4" /> {pdfName || "Recipe PDF attached"}
            </a>
            <button type="button" data-testid="recipe-pdf-remove" onClick={() => { setForm({ ...form, pdf_url: "" }); setPdfName(""); }} className="text-xs text-[#A8A29E] hover:text-red-500">Remove PDF</button>
          </div>
        )}
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button data-testid="recipe-save" type="submit" disabled={busy}
          className="bg-navy text-white rounded-full px-7 py-2.5 text-sm font-medium hover:bg-navy-dark flex items-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Publish to portal
        </button>
        <button data-testid="recipe-clear" type="button" onClick={clear}
          className="border border-[#E7E5E4] text-[#57534E] rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#F5F5F4]">
          Remove
        </button>
      </div>
    </form>
  );
}

function RecipeArchiveAdmin() {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/recipes/archive"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from the recipe archive?`)) return;
    try {
      await api.delete(`/admin/recipes/archive/${r.id}`);
      toast.success("Recipe removed from the archive");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove recipe");
    }
  };

  if (items === null || items.length === 0) return null;
  return (
    <div className="mb-8" data-testid="recipe-archive-admin">
      <h3 className="font-heading text-xl text-navy mb-1">Recipe archive ({items.length})</h3>
      <p className="text-xs text-[#78716C] mb-4">Every published Recipe of the Week is saved here automatically — members can browse them in the portal's "Recipe Archive" tab.</p>
      <div className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden">
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-4 px-5 py-3.5 border-b border-[#F5F5F4] last:border-0" data-testid={`admin-archive-${r.id}`}>
            {r.image_url ? (
              <img src={r.image_url} alt={r.title} className="w-10 h-10 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center shrink-0"><ChefHat className="w-4 h-4" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-navy truncate">{r.title}</div>
              <div className="text-xs text-[#78716C]">{new Date(r.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
            </div>
            <button onClick={() => del(r)} data-testid={`delete-archive-${r.id}`} className="text-[#A8A29E] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditContentModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ title: item.title || "", description: item.description || "", category: item.category, type: item.type });
  const [cats, setCats] = useState([]);
  const [srcMode, setSrcMode] = useState("keep"); // keep | link | upload
  const [linkUrl, setLinkUrl] = useState(item.external_url || "");
  const [file, setFile] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(item.thumbnail_url || "");
  const [newThumb, setNewThumb] = useState(null);
  const [attachUrl, setAttachUrl] = useState(item.attachment_url || "");
  const [attachName, setAttachName] = useState(item.attachment_name || "");
  const [newAttach, setNewAttach] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let turl = thumbUrl;
      if (newThumb) turl = await uploadImageFile(newThumb);
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("type", form.type);
      fd.append("thumbnail_url", turl || "");
      let aurl = attachUrl, aname = attachName;
      if (newAttach) { const a = await uploadPdfFile(newAttach); aurl = a.url; aname = a.name; }
      fd.append("attachment_url", aurl || "");
      fd.append("attachment_name", aurl ? (aname || "Printable PDF") : "");
      if (srcMode === "link") fd.append("external_url", linkUrl);
      if (srcMode === "upload" && file) fd.append("file", file);
      await api.put(`/admin/content/${item.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Content updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save changes");
    } finally { setBusy(false); }
  };

  const cls = "w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";
  const seg = (active) => `flex-1 rounded-md py-2 text-xs font-medium ${active ? "bg-white shadow-sm text-navy" : "text-[#78716C]"}`;
  const catInList = cats.some((c) => c.id === form.category);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-content-modal">
      <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-lg p-7 relative max-h-[90vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} data-testid="edit-cancel" className="absolute top-4 right-4 text-[#78716C] hover:text-navy"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-xl text-navy">Edit content</h3>
        <input required placeholder="Title" data-testid="edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={cls} />
        <textarea placeholder="Description" data-testid="edit-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={cls} />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={cls} data-testid="edit-category">
            {!catInList && <option value={form.category}>{form.category}</option>}
            {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={cls} data-testid="edit-type">
            <option value="video">Video</option><option value="pdf">PDF document</option>
          </select>
        </div>
        <div>
          <p className="text-xs text-[#78716C] mb-1.5">Video / PDF source — currently {item.external_url ? "a link" : `an uploaded file${item.original_filename ? ` (${item.original_filename})` : ""}`}</p>
          <div className="flex gap-2 p-1 bg-[#F5F5F4] rounded-lg">
            <button type="button" data-testid="edit-src-keep" onClick={() => setSrcMode("keep")} className={seg(srcMode === "keep")}>Keep current</button>
            <button type="button" data-testid="edit-src-link" onClick={() => setSrcMode("link")} className={seg(srcMode === "link")}>Change link</button>
            <button type="button" data-testid="edit-src-upload" onClick={() => setSrcMode("upload")} className={seg(srcMode === "upload")}>Upload new file</button>
          </div>
          {srcMode === "link" && (
            <input placeholder="https://… (video or PDF URL)" data-testid="edit-link-url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className={`${cls} mt-3`} required />
          )}
          {srcMode === "upload" && (
            <label className="mt-3 flex items-center gap-3 border-2 border-dashed border-[#E7E5E4] rounded-lg px-4 py-4 cursor-pointer hover:border-teal">
              <Upload className="w-5 h-5 text-teal" />
              <span className="text-sm text-[#57534E]">{file ? file.name : "Choose a new video or PDF"}</span>
              <input type="file" accept="video/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="edit-file" />
            </label>
          )}
        </div>
        <label className="flex items-center gap-3 border border-[#E7E5E4] rounded-lg px-4 py-3 cursor-pointer hover:border-teal transition-colors">
          {newThumb ? <img src={URL.createObjectURL(newThumb)} alt="New thumbnail" className="w-12 h-12 rounded-lg object-cover" />
            : thumbUrl ? <img src={thumbUrl} alt="Current thumbnail" className="w-12 h-12 rounded-lg object-cover" />
            : <ImagePlus className="w-5 h-5 text-teal" />}
          <span className="text-sm text-[#57534E] flex-1">{newThumb ? newThumb.name : thumbUrl ? "Preview thumbnail (click to replace)" : "Add a preview thumbnail (optional)"}</span>
          {(newThumb || thumbUrl) && (
            <button type="button" data-testid="edit-thumbnail-remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNewThumb(null); setThumbUrl(""); }}
              className="text-xs text-[#A8A29E] hover:text-red-500">Remove</button>
          )}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setNewThumb(e.target.files[0] || null)} data-testid="edit-thumbnail" />
        </label>
        <label className="flex items-center gap-3 border border-[#E7E5E4] rounded-lg px-4 py-3 cursor-pointer hover:border-teal transition-colors">
          <FileText className="w-5 h-5 text-teal" />
          <span className="text-sm text-[#57534E] flex-1">
            {newAttach ? newAttach.name : attachUrl ? `${attachName || "Printable PDF"} (click to replace)` : "Attach a printable PDF (optional)"}
          </span>
          {(newAttach || attachUrl) && (
            <button type="button" data-testid="edit-attachment-remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNewAttach(null); setAttachUrl(""); setAttachName(""); }}
              className="text-xs text-[#A8A29E] hover:text-red-500">Remove</button>
          )}
          <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setNewAttach(e.target.files[0] || null)} data-testid="edit-attachment" />
        </label>
        <button type="submit" disabled={busy} data-testid="edit-save"
          className="w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-dark flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
        </button>
      </form>
    </div>
  );
}

function ContentLibrary({ refreshKey }) {
  const [items, setItems] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/content"); setItems(data); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const del = async (id) => { await api.delete(`/admin/content/${id}`); load(); toast.success("Content removed"); };

  const dropOn = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const next = [...items];
    const from = next.findIndex((i) => i.id === dragId);
    const to = next.findIndex((i) => i.id === targetId);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setDragId(null);
    try {
      await api.put("/admin/content/reorder", { ids: next.map((i) => i.id) });
      toast.success("Order saved — members now see this order in the library");
    } catch {
      toast.error("Could not save the new order");
      load();
    }
  };

  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div data-testid="admin-content">
      <h3 className="font-heading text-xl text-navy mb-1">Content library ({items.length})</h3>
      {items.length > 1 && <p className="text-xs text-[#78716C] mb-4">Drag rows to set the order members see in the portal.</p>}
      <div className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden">
        {items.length === 0 && <p className="p-6 text-sm text-[#78716C]">No content yet. Add your first item.</p>}
        {items.map((i) => (
          <div key={i.id} draggable data-testid={`content-row-${i.id}`}
            onDragStart={() => setDragId(i.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => dropOn(i.id)}
            onDragEnd={() => setDragId(null)}
            className={`flex items-center gap-4 px-5 py-4 border-b border-[#F5F5F4] last:border-0 cursor-grab active:cursor-grabbing transition-colors ${dragId === i.id ? "opacity-50 bg-teal/5" : ""}`}>
            <GripVertical className="w-4 h-4 text-[#D6D3D1] shrink-0" />
            {i.thumbnail_url ? (
              <img src={i.thumbnail_url} alt={i.title} className="w-10 h-10 rounded-lg object-cover shrink-0" />
            ) : (
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${i.type === "video" ? "bg-navy text-white" : "bg-teal/10 text-teal"}`}>
                {i.type === "video" ? <Play className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-navy truncate">{i.title}</div>
              <div className="text-xs text-[#78716C] capitalize">{i.category} · {i.type} {i.external_url ? "· link" : "· uploaded"}{i.attachment_url && " · printable PDF"}</div>
            </div>
            <button onClick={() => setEditing(i)} data-testid={`edit-content-${i.id}`} title="Edit content"
              className="text-[#A8A29E] hover:text-teal"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(i.id)} data-testid={`delete-content-${i.id}`} className="text-[#A8A29E] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing && <EditContentModal item={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function UploadForm({ onDone }) {
  const [form, setForm] = useState({ title: "", description: "", category: "", type: "video", external_url: "" });
  const [file, setFile] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [attach, setAttach] = useState(null);
  const [cats, setCats] = useState([]);
  const [mode, setMode] = useState("link"); // link | upload
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCats(r.data);
      if (r.data.length) setForm((f) => ({ ...f, category: f.category || r.data[0].id }));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let thumbnailUrl = "";
      if (thumb) thumbnailUrl = await uploadImageFile(thumb);
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("type", form.type);
      if (thumbnailUrl) fd.append("thumbnail_url", thumbnailUrl);
      if (attach) {
        const a = await uploadPdfFile(attach);
        fd.append("attachment_url", a.url);
        fd.append("attachment_name", a.name);
      }
      if (mode === "link") fd.append("external_url", form.external_url);
      if (mode === "upload" && file) fd.append("file", file);
      await api.post("/admin/content", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Content added!");
      setForm({ title: "", description: "", category: cats[0]?.id || "", type: "video", external_url: "" });
      setFile(null); setThumb(null); setAttach(null);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm space-y-4" data-testid="upload-form">
      <h3 className="font-heading text-xl text-navy">Add new content</h3>
      <input required placeholder="Title" data-testid="upload-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
      <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" rows={6} />
      <div className="grid grid-cols-2 gap-3">
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2.5 text-sm" data-testid="upload-category">
          {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2.5 text-sm" data-testid="upload-type">
          <option value="video">Video</option><option value="pdf">PDF document</option>
        </select>
      </div>
      <label className="flex items-center gap-3 border border-[#E7E5E4] rounded-lg px-4 py-3 cursor-pointer hover:border-teal transition-colors">
        {thumb ? <img src={URL.createObjectURL(thumb)} alt="Thumbnail preview" className="w-12 h-12 rounded-lg object-cover" /> : <ImagePlus className="w-5 h-5 text-teal" />}
        <span className="text-sm text-[#57534E] flex-1">{thumb ? thumb.name : "Preview thumbnail image (optional)"}</span>
        {thumb && (
          <button type="button" data-testid="upload-thumbnail-remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setThumb(null); }}
            className="text-xs text-[#A8A29E] hover:text-red-500">Remove</button>
        )}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => setThumb(e.target.files[0] || null)} data-testid="upload-thumbnail" />
      </label>
      <label className="flex items-center gap-3 border border-[#E7E5E4] rounded-lg px-4 py-3 cursor-pointer hover:border-teal transition-colors">
        <FileText className="w-5 h-5 text-teal" />
        <span className="text-sm text-[#57534E] flex-1">{attach ? attach.name : "Attach a printable PDF (optional) — members get a download button"}</span>
        {attach && (
          <button type="button" data-testid="upload-attachment-remove" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttach(null); }}
            className="text-xs text-[#A8A29E] hover:text-red-500">Remove</button>
        )}
        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setAttach(e.target.files[0] || null)} data-testid="upload-attachment" />
      </label>
      <div className="flex gap-2 p-1 bg-[#F5F5F4] rounded-lg">
        <button type="button" onClick={() => setMode("link")} className={`flex-1 rounded-md py-2 text-sm font-medium ${mode === "link" ? "bg-white shadow-sm text-navy" : "text-[#78716C]"}`}>Paste link / URL</button>
        <button type="button" onClick={() => setMode("upload")} className={`flex-1 rounded-md py-2 text-sm font-medium ${mode === "upload" ? "bg-white shadow-sm text-navy" : "text-[#78716C]"}`}>Upload file</button>
      </div>
      {mode === "link" ? (
        <input placeholder="https://… (video or PDF URL)" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} className="w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm" data-testid="upload-url" />
      ) : (
        <label className="flex items-center gap-3 border-2 border-dashed border-[#E7E5E4] rounded-lg px-4 py-5 cursor-pointer hover:border-teal">
          <Upload className="w-5 h-5 text-teal" />
          <span className="text-sm text-[#57534E]">{file ? file.name : "Choose a video or PDF to upload"}</span>
          <input type="file" accept="video/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file" />
        </label>
      )}
      <button type="submit" disabled={busy} data-testid="upload-submit" className="w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-dark flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Publish content
      </button>
    </form>
  );
}

function CategoryManager({ onChanged }) {
  const [cats, setCats] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/categories"); setCats(data); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/categories", { label: label.trim() });
      toast.success(`Category "${label.trim()}" added`);
      setLabel(""); load(); onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add category");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Remove the "${c.label}" category?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category removed"); load(); onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove category");
    }
  };

  const dropOn = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const next = [...cats];
    const from = next.findIndex((c) => c.id === dragId);
    const to = next.findIndex((c) => c.id === targetId);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setCats(next);
    setDragId(null);
    try {
      await api.put("/admin/categories/reorder", { ids: next.map((c) => c.id) });
      toast.success("Category order saved");
      onChanged();
    } catch {
      toast.error("Could not save the new order");
      load();
    }
  };

  return (
    <div className="max-w-2xl bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm" data-testid="category-manager">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-navy/10 text-navy flex items-center justify-center"><Tags className="w-5 h-5" /></div>
        <div>
          <h3 className="font-heading text-xl text-navy">Content categories</h3>
          <p className="text-xs text-[#78716C]">Add, remove or drag to reorder the categories members see. Categories with content can't be removed.</p>
        </div>
      </div>
      <form onSubmit={add} className="mt-5 flex gap-3">
        <input data-testid="category-add-input" placeholder="New category name (e.g. Workouts)" value={label} onChange={(e) => setLabel(e.target.value)}
          className="flex-1 border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
        <button data-testid="category-add-btn" type="submit" disabled={busy || !label.trim()}
          className="inline-flex items-center gap-1.5 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-dark disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </form>
      <div className="mt-4 flex flex-wrap gap-2">
        {cats === null ? <Loader2 className="w-5 h-5 animate-spin text-teal" /> : cats.map((c) => (
          <span key={c.id} data-testid={`category-chip-${c.id}`} draggable
            onDragStart={() => setDragId(c.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => dropOn(c.id)}
            onDragEnd={() => setDragId(null)}
            className={`inline-flex items-center gap-1.5 bg-[#F5F5F4] border border-[#E7E5E4] rounded-full pl-2.5 pr-2 py-1.5 text-sm text-navy font-medium cursor-grab active:cursor-grabbing transition-opacity ${dragId === c.id ? "opacity-50 border-teal" : ""}`}>
            <GripVertical className="w-3.5 h-3.5 text-[#D6D3D1]" />
            {c.label}
            <button onClick={() => del(c)} data-testid={`category-delete-${c.id}`} title={`Remove ${c.label}`}
              className="w-5 h-5 rounded-full flex items-center justify-center text-[#A8A29E] hover:text-white hover:bg-red-500 transition-colors">
              <Trash2 className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function LeadsPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/leads").then((r) => setItems(r.data)); }, []);

  const exportLeadsCsv = async () => {
    const { data } = await api.get("/admin/leads/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "mbbc_learn_more_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Leads CSV downloaded");
  };

  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading text-xl text-navy mb-1.5">Learn More leads ({items.length})</h3>
          <p className="text-sm text-[#78716C]">Each lead received the auto-responder email and is auto-synced to the "MBBC Learn More Leads" group in MailerLite.</p>
        </div>
        <button onClick={exportLeadsCsv} data-testid="export-leads-btn" disabled={items.length === 0}
          className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-dark disabled:opacity-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      {items.length === 0 && (
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-8 text-sm text-[#78716C]">
          No leads yet. When a visitor completes the Learn More form on the homepage, they'll appear here.
        </div>
      )}
      <div className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden">
        {items.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-4 border-b border-[#F5F5F4] last:border-0" data-testid={`lead-${l.id}`}>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-navy">{l.name}</span>
              <span className="ml-2 text-sm text-[#78716C]">{l.email}</span>
            </div>
            <div className="text-xs text-[#78716C]">
              {new Date(l.created_at).toLocaleString("en-GB")}
              {l.email_sent ? <span className="ml-2 text-teal">· emailed</span> : <span className="ml-2 text-amber-600">· email failed</span>}
              {l.mailerlite_synced ? <span className="ml-2 text-teal">· MailerLite ✓</span> : <span className="ml-2 text-[#A8A29E]">· not synced</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Enquiries() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/therapist-enquiries").then((r) => setItems(r.data)); }, []);
  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div data-testid="admin-enquiries">
      <h3 className="font-heading text-xl text-navy mb-4">Therapist interest enquiries ({items.length})</h3>
      {items.length === 0 && (
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-8 text-sm text-[#78716C]">
          No enquiries yet. When someone completes the "Register your interest" form on the For Therapists page, it will appear here.
        </div>
      )}
      <div className="space-y-4">
        {items.map((e) => (
          <div key={e.id} className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid={`enquiry-${e.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-heading text-lg text-navy">{e.name}</div>
              <div className="text-xs text-[#78716C]">{new Date(e.created_at).toLocaleString("en-GB")}
                {e.email_sent ? <span className="ml-2 text-teal">· emailed</span> : <span className="ml-2 text-amber-600">· email pending</span>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-[#57534E]">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-teal" />{e.email}</span>
              {e.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-teal" />{e.phone}</span>}
              {e.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-teal" />{e.location}</span>}
            </div>
            {e.qualifications && <p className="mt-3 text-sm text-[#44403C]"><strong className="text-navy">Qualifications:</strong> {e.qualifications}</p>}
            {e.message && <p className="mt-2 text-sm text-[#44403C]"><strong className="text-navy">Message:</strong> {e.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

const PLAN_OPTIONS = [
  { id: "virtual", label: "Virtual Membership — £18/month (manual payment)" },
  { id: "physical", label: "Physical Membership — £28/month (manual payment)" },
  { id: "hybrid", label: "Hybrid Membership — £38/month (manual payment)" },
  { id: "inner_circle", label: "Inner Circle — £449 one-off (manual payment)" },
  { id: "bolt_on", label: "Practitioner Bolt-On — £65 one-off (8-week access)" },
  { id: "complimentary", label: "Complimentary — free access" },
];

function MembershipDialog({ u, onClose, onChanged }) {
  const [plan, setPlan] = useState("virtual");
  const [busy, setBusy] = useState(false);
  const [pracRef, setPracRef] = useState(u.practitioner_ref || "");
  const [note, setNote] = useState("");
  const [payments, setPayments] = useState(null);
  const hasMembership = u.membership_status === "active" || u.membership_status === "paused";
  const loadPayments = () => api.get(`/admin/users/${u.id}/payments`).then((r) => setPayments(r.data)).catch(() => setPayments([]));
  useEffect(() => { loadPayments(); }, [u.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const voidPayment = async (p) => {
    const vNote = window.prompt(`Void the ${p.plan_name || p.plan} payment of £${Number(p.amount).toFixed(2)}?\n\nThis removes it from revenue and invoices (the membership itself is not changed).\n\nOptional note:`, "");
    if (vNote === null) return;
    try {
      await api.post(`/admin/payments/${p.id}/void`, { note: vNote.trim() || null });
      toast.success("Payment voided — removed from financial totals");
      loadPayments();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not void the payment");
    }
  };
  const unvoidPayment = async (p) => {
    if (!window.confirm(`Restore the ${p.plan_name || p.plan} payment of £${Number(p.amount).toFixed(2)}? It will count in revenue and invoices again.`)) return;
    try {
      await api.post(`/admin/payments/${p.id}/unvoid`);
      toast.success("Payment restored");
      loadPayments();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not restore the payment");
    }
  };
  const assign = async () => {
    const label = PLAN_OPTIONS.find((p) => p.id === plan)?.label;
    if (!window.confirm(`Assign "${label}" to ${u.email}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/membership`, { plan, practitioner_ref: pracRef.trim() || null });
      toast.success(`${u.name || u.email} now has ${plan.replace("_", " ")} membership`);
      await onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update membership");
    } finally { setBusy(false); }
  };
  const restartBilling = async () => {
    if (!window.confirm(`Restart ${u.email}'s Stripe billing now? Any pause on their subscription is removed and monthly payments resume on their normal billing date.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resume-billing`);
      toast.success(`Billing restarted — Stripe subscription is ${data.stripe_status}`);
      await onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not restart billing");
    } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(`Cancel ${u.email}'s membership? This removes portal access and cancels any Stripe subscription so they're never charged again.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/cancel-membership`, { note: note.trim() || null });
      toast.success(data.stripe_cancelled ? "Membership cancelled — Stripe billing stopped too" : "Membership cancelled");
      await onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel membership");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="membership-dialog">
        <h3 className="font-heading text-xl text-navy">Manage membership</h3>
        <p className="mt-1 text-sm text-[#78716C]">{u.name || u.email} — currently <strong className="text-navy">{u.membership_plan ? u.membership_plan.replace("_", " ") : "no membership"}</strong> ({u.membership_status || "inactive"}){u.practitioner_ref ? <> · via <strong className="text-navy">{u.practitioner_ref}</strong></> : null}</p>
        <label className="block mt-5 text-xs font-semibold uppercase tracking-wide text-[#78716C]">Assign membership</label>
        <select data-testid="membership-plan-select" value={plan} onChange={(e) => setPlan(e.target.value)}
          className="mt-2 w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal">
          {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {plan !== "complimentary" && (
          <input data-testid="membership-practitioner-input" value={pracRef} onChange={(e) => setPracRef(e.target.value)}
            placeholder="Practitioner reference (optional — if paying via a practitioner)"
            className="mt-3 w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
        )}
        <p className="mt-3 text-xs text-[#78716C] leading-relaxed">
          {plan === "inner_circle"
            ? "Works exactly like an online purchase: takes one of the 6 places, pauses any £18 Stripe subscription until early December, sends the Inner Circle welcome email and adds them to the MailerLite Inner Circle group."
            : plan === "bolt_on"
            ? "Exclusively for practitioner-referred clients — practitioner reference required. £65 one-off manual payment, 8 weeks of full platform access (then access ends automatically). Appears on the practitioner's running Bolt-On invoice immediately."
            : plan === "complimentary"
            ? "Free full access — ideal for testers. Added to the MailerLite beta group."
            : "Use after taking a manual payment (cash/bank transfer/practitioner). Records the payment against the account and sends the membership confirmation email. Access runs until you cancel it here."}
        </p>
        <button disabled={busy} onClick={assign} data-testid="membership-assign-btn"
          className="mt-4 w-full bg-navy text-white rounded-full py-3 text-sm font-semibold hover:bg-navy-dark disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Assign membership
        </button>
        {u.stripe_subscription_id && (
          <div className="mt-5 pt-5 border-t border-[#E7E5E4]">
            <button disabled={busy} onClick={restartBilling} data-testid="membership-restart-billing-btn"
              className="w-full border border-teal/40 text-teal hover:bg-teal/5 rounded-full py-3 text-sm font-semibold disabled:opacity-60">
              Restart Stripe billing (removes any pause)
            </button>
            <p className="mt-2 text-[11px] text-[#A8A29E] text-center">Use this if their monthly payments stopped collecting — e.g. after an Inner Circle upgrade was reversed. Missed months aren't charged retroactively.</p>
          </div>
        )}
        {hasMembership && (
          <div className="mt-5 pt-5 border-t border-[#E7E5E4]">
            <input data-testid="membership-cancel-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder='Cancellation note (optional, e.g. "changed mind, refunded manually")'
              className="w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300" />
            <button disabled={busy} onClick={cancel} data-testid="membership-cancel-btn"
              className="mt-3 w-full border border-red-300 text-red-600 hover:bg-red-50 rounded-full py-3 text-sm font-semibold disabled:opacity-60">
              Cancel membership (stops Stripe billing too)
            </button>
          </div>
        )}
        <div className="mt-5 pt-5 border-t border-[#E7E5E4]" data-testid="payment-history">
          <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#78716C]"><Receipt className="w-3.5 h-3.5" /> Payment history</h4>
          {payments === null ? <Loader2 className="mt-3 w-4 h-4 animate-spin text-teal" /> : payments.length === 0 ? (
            <p className="mt-2 text-xs text-[#A8A29E]">No payments recorded for this member.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {payments.map((p) => (
                <div key={p.id} className={`rounded-lg border px-3.5 py-2.5 text-xs ${p.voided_by_admin ? "border-red-100 bg-red-50/40" : "border-[#F5F5F4] bg-[#FAFAF9]"}`} data-testid={`payment-row-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-medium ${p.voided_by_admin ? "text-[#A8A29E] line-through" : "text-navy"}`}>{p.plan_name || p.plan} — £{Number(p.amount).toFixed(2)}</span>
                    <span className="text-[#A8A29E] shrink-0">{new Date(p.created_at).toLocaleDateString("en-GB")}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${p.payment_method === "manual_admin" ? "bg-amber-100 text-amber-700" : "bg-teal/10 text-teal"}`}>{p.payment_method === "manual_admin" ? "Manual" : "Online"}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-[#F5F5F4] text-[#78716C]"}`}>{p.payment_status}</span>
                    {p.practitioner_ref && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-navy/10 text-navy">via {p.practitioner_ref}</span>}
                    {p.voided_by_admin && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-600">Voided{p.voided_at ? ` ${new Date(p.voided_at).toLocaleDateString("en-GB")}` : ""}</span>}
                    {p.payment_status === "paid" && !p.voided_by_admin && (
                      <button onClick={() => voidPayment(p)} data-testid={`void-payment-${p.id}`}
                        className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-red-400 hover:text-red-600 border border-red-200 hover:border-red-400 rounded-full px-2 py-0.5 transition-colors">
                        Void
                      </button>
                    )}
                    {p.voided_by_admin && (
                      <button onClick={() => unvoidPayment(p)} data-testid={`unvoid-payment-${p.id}`}
                        className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-emerald-600 hover:text-emerald-700 border border-emerald-200 hover:border-emerald-400 rounded-full px-2 py-0.5 transition-colors">
                        Restore
                      </button>
                    )}
                  </div>
                  {p.void_note && <p className="mt-1.5 text-[11px] italic text-red-500">"{p.void_note}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={onClose} data-testid="membership-close-btn" className="mt-4 w-full text-sm text-[#78716C] hover:text-navy">Close</button>
      </div>
    </div>
  );
}

function UsersPanel() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client", plan: "complimentary", practitioner_ref: "" });
  const [presets, setPresets] = useState([]);
  const [presetForm, setPresetForm] = useState({ practitioner_ref: "", plan: "virtual" });
  const [presetBusy, setPresetBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [manageUser, setManageUser] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/admin/users"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);
  const loadPresets = useCallback(async () => {
    try { const { data } = await api.get("/admin/practitioner-presets"); setPresets(data); } catch { setPresets([]); }
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const addPreset = async (e) => {
    e.preventDefault();
    if (!presetForm.practitioner_ref.trim()) { toast.error("Enter the practitioner's name"); return; }
    setPresetBusy(true);
    try {
      await api.post("/admin/practitioner-presets", presetForm);
      toast.success(`Quick-pick added for ${presetForm.practitioner_ref.trim()}`);
      setPresetForm({ practitioner_ref: "", plan: "virtual" });
      loadPresets();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Could not add the quick-pick");
    } finally { setPresetBusy(false); }
  };
  const removePreset = async (p) => {
    if (!window.confirm(`Remove the quick-pick "${p.label}"? Existing clients and invoicing are not affected.`)) return;
    try {
      await api.delete(`/admin/practitioner-presets/${p.id}`);
      if (form.plan === `preset:${p.id}`) setForm({ ...form, plan: "complimentary" });
      loadPresets();
      toast.success("Quick-pick removed");
    } catch { toast.error("Could not remove the quick-pick"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    const preset = form.plan.startsWith("preset:") ? presets.find((p) => `preset:${p.id}` === form.plan) : null;
    const payload = {
      ...form,
      plan: preset ? preset.plan : form.plan,
      practitioner_ref: preset ? preset.practitioner_ref : (form.practitioner_ref.trim() || null),
      origin_url: window.location.origin,
    };
    try {
      await api.post("/admin/users", payload);
      toast.success(preset
        ? `Account created for ${form.email} — ${preset.label}. Login details emailed.`
        : form.role === "client" && form.plan !== "complimentary"
        ? `Account created for ${form.email} with ${form.plan.replace("_", " ")} membership — login details emailed`
        : `Free account created for ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "client", plan: "complimentary", practitioner_ref: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create user");
    } finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    toast.success("User deleted");
    load();
  };

  const sendCreds = async (u) => {
    if (!window.confirm(`Email login details to ${u.email}? This sets a new temporary password for the account.`)) return;
    setSendingId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/send-credentials`, { origin_url: window.location.origin });
      toast.success(`Login details emailed to ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send email");
    } finally { setSendingId(null); }
  };

  const cls = "w-full border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";
  return (
    <div className="space-y-8" data-testid="admin-users">
      <SubscribersCard />
      <form onSubmit={create} className="max-w-2xl bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm space-y-4" data-testid="create-user-form">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center"><UserPlus className="w-5 h-5" /></div>
          <div>
            <h3 className="font-heading text-xl text-navy">Create an account</h3>
            <p className="text-xs text-[#78716C]">Create complimentary tester accounts or add paying clients manually — including clients referred by a collaborating practitioner.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="create-user-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} />
          <input data-testid="create-user-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="create-user-password" required minLength={6} placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={cls} />
          <select data-testid="create-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={cls}>
            <option value="client">Client (member portal)</option>
            <option value="nutrition_therapist">Nutritional Therapist (dashboard)</option>
          </select>
        </div>
        {form.role === "client" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="create-user-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className={cls}>
              <option value="complimentary">Complimentary — free access</option>
              <option value="virtual">Virtual Membership — £18/month (manual payment)</option>
              <option value="physical">Physical Membership — £28/month (manual payment)</option>
              <option value="hybrid">Hybrid Membership — £38/month (manual payment)</option>
              <option value="inner_circle">Inner Circle — £449 one-off (manual payment)</option>
              <option value="bolt_on">Practitioner Bolt-On — £65 one-off (8-week access)</option>
              {presets.map((p) => <option key={p.id} value={`preset:${p.id}`}>{p.label}</option>)}
            </select>
            <input data-testid="create-user-practitioner"
              placeholder="Practitioner reference (optional)"
              value={form.plan.startsWith("preset:") ? (presets.find((p) => `preset:${p.id}` === form.plan)?.practitioner_ref || "") : form.practitioner_ref}
              disabled={form.plan.startsWith("preset:")}
              onChange={(e) => setForm({ ...form, practitioner_ref: e.target.value })} className={`${cls} disabled:bg-[#F5F5F4] disabled:text-[#78716C]`} />
          </div>
        )}
        {form.role === "client" && (form.plan !== "complimentary" || form.practitioner_ref.trim()) && (
          <p className="text-xs text-[#78716C] -mt-1">
            {form.plan.startsWith("preset:")
              ? `Billed to "${presets.find((p) => `preset:${p.id}` === form.plan)?.practitioner_ref}" in the Practitioners section for invoicing. `
              : form.practitioner_ref.trim() ? `This client will appear under "${form.practitioner_ref.trim()}" in the Practitioners section for monthly invoicing. ` : ""}
            Their login details will be emailed to them straight away.
          </p>
        )}
        <button data-testid="create-user-submit" type="submit" disabled={busy}
          className="w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-dark flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create account
        </button>
      </form>

      <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid="preset-manager">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-navy text-white flex items-center justify-center"><Handshake className="w-5 h-5" /></div>
          <div>
            <h3 className="font-heading text-xl text-navy">Practitioner quick-picks</h3>
            <p className="text-xs text-[#78716C]">Add a collaborating practitioner once and they appear in the membership dropdown above — one click sets the plan and invoicing reference.</p>
          </div>
        </div>
        {presets.length > 0 && (
          <div className="mt-4 space-y-2">
            {presets.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#F5F5F4] bg-[#FAFAF9] px-4 py-2.5 text-sm" data-testid={`preset-row-${p.id}`}>
                <span className="text-navy font-medium truncate">{p.label}</span>
                <button onClick={() => removePreset(p)} data-testid={`preset-delete-${p.id}`} className="text-[#A8A29E] hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={addPreset} className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <input data-testid="preset-name-input" placeholder="Practitioner / programme name" value={presetForm.practitioner_ref}
            onChange={(e) => setPresetForm({ ...presetForm, practitioner_ref: e.target.value })} className={cls} />
          <select data-testid="preset-plan-select" value={presetForm.plan} onChange={(e) => setPresetForm({ ...presetForm, plan: e.target.value })} className={cls}>
            <option value="virtual">Virtual — £18/month</option>
            <option value="physical">Physical — £28/month</option>
            <option value="hybrid">Hybrid — £38/month</option>
            <option value="bolt_on">Bolt-On — £65 one-off (8-week access)</option>
            <option value="inner_circle">Inner Circle — £449 one-off</option>
          </select>
          <button type="submit" disabled={presetBusy} data-testid="preset-add-btn"
            className="inline-flex items-center justify-center gap-2 bg-navy text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-navy-dark disabled:opacity-60">
            {presetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </form>
      </div>

      <div>
        <h3 className="font-heading text-xl text-navy mb-4">All users ({items ? items.length : "…"})</h3>
        {items === null ? <Loader2 className="w-6 h-6 animate-spin text-teal" /> : (
          <div className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden">
            {items.length === 0 && <p className="p-6 text-sm text-[#78716C]">No users yet.</p>}
            {items.map((u) => (
              <div key={u.id} className="flex items-center gap-4 px-5 py-4 border-b border-[#F5F5F4] last:border-0" data-testid={`user-row-${u.id}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${u.role === "nutrition_therapist" ? "bg-navy text-white" : "bg-teal/10 text-teal"}`}>
                  {u.role === "nutrition_therapist" ? <Stethoscope className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-navy truncate">{u.name || u.email}</span>
                    {u.complimentary && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5" data-testid={`complimentary-badge-${u.id}`}>
                        <Gift className="w-3 h-3" /> Complimentary
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#78716C]">{u.email} · {u.role === "nutrition_therapist" ? "Therapist" : "Client"} · {u.membership_status || "inactive"}{u.membership_plan ? ` · ${u.membership_plan}` : ""}{u.credentials_sent_at ? " · login details sent" : ""}</div>
                </div>
                {u.complimentary && (
                  <button onClick={() => sendCreds(u)} disabled={sendingId === u.id} data-testid={`send-credentials-${u.id}`}
                    title="Email login details (sets a new temporary password)"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-teal hover:text-teal-dark border border-teal/30 hover:border-teal rounded-full px-3 py-1.5 transition-colors disabled:opacity-50 shrink-0">
                    {sendingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send login details
                  </button>
                )}
                {u.role === "client" && (
                  <button onClick={() => setManageUser(u)} data-testid={`manage-membership-${u.id}`}
                    title="Assign or cancel this member's membership"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-navy hover:text-teal border border-[#E7E5E4] hover:border-teal rounded-full px-3 py-1.5 transition-colors shrink-0">
                    <BadgeCheck className="w-3.5 h-3.5" /> Membership
                  </button>
                )}
                <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`} className="text-[#A8A29E] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {manageUser && <MembershipDialog u={manageUser} onClose={() => setManageUser(null)} onChanged={load} />}
    </div>
  );
}

function PractitionerCard({ p }) {
  const [email, setEmail] = useState(p.email || "");
  const [busyKey, setBusyKey] = useState(null);
  const download = async (month, invoiceType) => {
    const key = `dl|${invoiceType}|${month || "all"}`;
    setBusyKey(key);
    try {
      const { data } = await api.get("/admin/practitioners/invoice",
        { params: { name: p.name, month, invoice_type: invoiceType }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const slug = p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      a.download = invoiceType === "bolt_on" ? `mbbc-bolt-on-invoice-${slug}.pdf` : `mbbc-invoice-${slug}-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch {
      toast.error("Could not generate the invoice");
    } finally { setBusyKey(null); }
  };
  const sendInvoice = async (month, invoiceType) => {
    const to = email.trim();
    if (!to) { toast.error(`Add ${p.name}'s email address first`); return; }
    if (!window.confirm(`Email the ${invoiceType === "bolt_on" ? "Bolt-On running" : month} invoice to ${to}?`)) return;
    const key = `em|${invoiceType}|${month || "all"}`;
    setBusyKey(key);
    try {
      await api.post("/admin/practitioners/send-invoice", { name: p.name, email: to, invoice_type: invoiceType, month });
      toast.success(`Invoice emailed to ${to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send the invoice email");
    } finally { setBusyKey(null); }
  };
  const btnCls = "inline-flex items-center justify-center gap-1.5 text-xs font-medium border rounded-full px-3 py-1.5 transition-colors disabled:opacity-50";
  const actionBtns = (month, invoiceType) => (
    <span className="flex gap-2">
      <button onClick={() => download(month, invoiceType)} disabled={busyKey === `dl|${invoiceType}|${month || "all"}`}
        data-testid={`download-invoice-${p.name}-${month || "bolt-on"}`}
        className={`${btnCls} text-teal hover:text-teal-dark border-teal/30 hover:border-teal`}>
        {busyKey === `dl|${invoiceType}|${month || "all"}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Invoice
      </button>
      <button onClick={() => sendInvoice(month, invoiceType)} disabled={busyKey === `em|${invoiceType}|${month || "all"}`}
        data-testid={`email-invoice-${p.name}-${month || "bolt-on"}`}
        className={`${btnCls} text-navy hover:text-teal border-[#E7E5E4] hover:border-teal`}>
        {busyKey === `em|${invoiceType}|${month || "all"}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Email
      </button>
    </span>
  );
  return (
    <div className="bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm" data-testid={`practitioner-card-${p.name}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-navy text-white flex items-center justify-center"><Handshake className="w-5 h-5" /></div>
          <div>
            <h4 className="font-heading text-lg text-navy">{p.name}</h4>
            <p className="text-xs text-[#78716C]">{p.active_clients} active client{p.active_clients === 1 ? "" : "s"}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide font-semibold text-[#78716C]">Total to date (memberships + bolt-ons)</div>
          <div className="font-heading text-2xl text-navy" data-testid={`practitioner-total-${p.name}`}>{money(p.total_to_date + (p.bolt_on?.total || 0))}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 max-w-md">
        <Mail className="w-4 h-4 text-[#A8A29E] shrink-0" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Practitioner's email for invoices"
          data-testid={`practitioner-email-${p.name}`}
          className="flex-1 border border-[#E7E5E4] rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
      </div>
      {p.months.length > 0 && (
        <div className="mt-5 border border-[#F5F5F4] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center bg-[#F5F5F4] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-[#78716C]">
            <span>Membership month</span><span className="text-right w-16">Clients</span><span className="text-right w-20">Amount</span><span className="w-44" />
          </div>
          {p.months.map((m) => (
            <div key={m.month} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-3 border-t border-[#F5F5F4] text-sm" data-testid={`practitioner-month-${p.name}-${m.month}`}>
              <span className="font-medium text-navy">{m.label}</span>
              <span className="text-right w-16 text-[#57534E]">{m.clients}</span>
              <span className="text-right w-20 font-semibold text-navy">{money(m.amount)}</span>
              {actionBtns(m.month, "month")}
            </div>
          ))}
        </div>
      )}
      {p.bolt_on?.count > 0 && (
        <div className="mt-5 border rounded-xl overflow-hidden" style={{ borderColor: "#C9A22755" }} data-testid={`bolt-on-section-${p.name}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" style={{ background: "#C9A22715" }}>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#8a6d12" }}>Bolt-On — 8 Weeks to Make a Difference</span>
              <p className="text-xs text-[#78716C] mt-0.5">{p.bolt_on.count} client{p.bolt_on.count === 1 ? "" : "s"} · running total <strong className="text-navy">{money(p.bolt_on.total)}</strong></p>
            </div>
            {actionBtns(null, "bolt_on")}
          </div>
          {p.bolt_on.clients.map((c, i) => (
            <div key={`${c.email}-${i}`} className="flex items-center justify-between px-4 py-2.5 border-t border-[#F5F5F4] text-sm">
              <span className="font-medium text-navy">{c.client}</span>
              <span className="text-xs text-[#78716C]">added {new Date(c.date).toLocaleDateString("en-GB")}</span>
              <span className="font-semibold text-navy w-20 text-right">{money(c.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PractitionersPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/practitioners").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div data-testid="admin-practitioners">
      <h3 className="font-heading text-xl text-navy mb-1">Practitioner invoicing</h3>
      <p className="text-sm text-[#78716C] mb-6 max-w-2xl">
        Clients added with a practitioner reference are billed to that practitioner — the full monthly fee for every calendar
        month they're active (Inner Circle appears once, in the joining month). £65 Bolt-On clients appear on a separate running
        invoice the moment they're added. Download or email the MBBC invoice straight from here.
      </p>
      {items.length === 0 && (
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-8 text-sm text-[#78716C]" data-testid="no-practitioners">
          No practitioner-referred clients yet. Add one in the Users tab — create a client (or use "Membership" on an existing one)
          with a paid plan or the £65 Bolt-On and a practitioner reference name, and they'll appear here.
        </div>
      )}
      <div className="space-y-6">
        {items.map((p) => <PractitionerCard key={p.name} p={p} />)}
      </div>
    </div>
  );
}

function FeedbackPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/feedback").then((r) => setItems(r.data)); }, []);
  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div data-testid="admin-feedback">
      <h3 className="font-heading text-xl text-navy mb-4">Tester feedback ({items.length})</h3>
      {items.length === 0 && (
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-8 text-sm text-[#78716C]">
          No feedback yet. Logged-in members and therapists can send feedback via the "Report an issue" button.
        </div>
      )}
      <div className="space-y-4">
        {items.map((f) => (
          <div key={f.id} className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid={`feedback-${f.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-navy">{f.user_name || f.user_email} <span className="text-xs text-[#78716C] font-normal">({f.user_email} · {f.user_role})</span></div>
              <div className="text-xs text-[#78716C]">{new Date(f.created_at).toLocaleString("en-GB")}</div>
            </div>
            {f.page && <div className="mt-1.5 text-xs text-teal font-medium">Page: {f.page}</div>}
            <p className="mt-3 text-sm text-[#44403C] whitespace-pre-wrap">{f.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersCard() {
  const [subs, setSubs] = useState(null);
  useEffect(() => { api.get("/admin/newsletter-subscribers").then((r) => setSubs(r.data)); }, []);

  const exportCsv = async () => {
    const { data } = await api.get("/admin/newsletter-subscribers/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "mbbc_newsletter_subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Subscriber CSV downloaded — import it into MailerLite");
  };

  return (
    <div className="max-w-2xl bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm" data-testid="subscribers-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Mail className="w-5 h-5" /></div>
          <div>
            <h3 className="font-heading text-xl text-navy">Newsletter subscribers</h3>
            <p className="text-xs text-[#78716C]">{subs === null ? "Loading…" : `${subs.length} subscriber${subs.length === 1 ? "" : "s"} opted in via signup & checkout`}</p>
          </div>
        </div>
        <button onClick={exportCsv} data-testid="export-subscribers-btn" disabled={!subs || subs.length === 0}
          className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-dark disabled:opacity-50">
          <Download className="w-4 h-4" /> Export CSV for MailerLite
        </button>
      </div>
      {subs && subs.length > 0 && (
        <div className="mt-5 max-h-56 overflow-y-auto border border-[#F5F5F4] rounded-lg">
          {subs.map((s) => (
            <div key={s.email} className="flex items-center justify-between px-4 py-2.5 border-b border-[#F5F5F4] last:border-0 text-sm">
              <div className="min-w-0">
                <span className="text-navy font-medium">{s.email}</span>
                {s.name && <span className="ml-2 text-[#78716C]">{s.name}</span>}
              </div>
              <span className="text-xs text-[#A8A29E] capitalize shrink-0">{(s.source || "").replace("_", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function MessagesPanel() {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/admin/therapist-messages"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (m) => {
    if (!window.confirm(`Delete this message from ${m.user_name || m.user_email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/therapist-messages/${m.id}`);
      toast.success("Message deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete message");
    }
  };

  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div data-testid="admin-messages">
      <h3 className="font-heading text-xl text-navy mb-4">Member messages ({items.length})</h3>
      {items.length === 0 && (
        <div className="bg-white border border-[#E7E5E4] rounded-xl p-8 text-sm text-[#78716C]">
          No messages yet. When a member uses the "Contact therapist" button in the portal, their message will appear here (and in Andrea's inbox).
        </div>
      )}
      <div className="space-y-4">
        {items.map((m) => (
          <div key={m.id} className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm" data-testid={`message-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-heading text-lg text-navy">{m.subject}</div>
              <div className="text-xs text-[#78716C]">{new Date(m.created_at).toLocaleString("en-GB")}
                {m.email_sent ? <span className="ml-2 text-teal">· emailed</span> : <span className="ml-2 text-amber-600">· email failed</span>}
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm text-[#57534E]">
              <Mail className="w-3.5 h-3.5 text-teal" />
              {m.user_name || m.user_email} <span className="text-xs text-[#A8A29E]">({m.user_email})</span>
            </div>
            <p className="mt-3 text-sm text-[#44403C] whitespace-pre-wrap">{m.message}</p>
            <div className="mt-4 flex items-center justify-between">
              <a href={`mailto:${m.user_email}?subject=Re: ${encodeURIComponent(m.subject)}`} data-testid={`reply-${m.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-teal-dark">
                <Send className="w-3.5 h-3.5" /> Reply by email
              </a>
              <button onClick={() => del(m)} data-testid={`delete-message-${m.id}`} title="Delete message"
                className="inline-flex items-center gap-1.5 text-sm text-[#A8A29E] hover:text-red-500">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCMS() {
  usePageTitle("Admin CMS");
  const [active, setActive] = useState("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [catsKey, setCatsKey] = useState(0);
  const [recipesKey, setRecipesKey] = useState(0);
  return (
    <DashboardLayout subtitle="Admin CMS" navItems={NAV} active={active} onNav={setActive}>
      {active === "overview" && <Overview />}
      {active === "content" && (<>
        <RecipeOfWeekEditor onPublished={() => setRecipesKey((k) => k + 1)} />
        <RecipeArchiveAdmin key={recipesKey} />
        <ContentLibrary refreshKey={refreshKey} />
      </>)}
      {active === "upload" && (
        <div className="space-y-8">
          <UploadForm key={catsKey} onDone={() => { setRefreshKey((k) => k + 1); setActive("content"); }} />
          <CategoryManager onChanged={() => setCatsKey((k) => k + 1)} />
        </div>
      )}
      {active === "enquiries" && <Enquiries />}
      {active === "leads" && <LeadsPanel />}
      {active === "messages" && <MessagesPanel />}
      {active === "users" && <UsersPanel />}
      {active === "practitioners" && <PractitionersPanel />}
      {active === "feedback" && <FeedbackPanel />}
    </DashboardLayout>
  );
}
